import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { StatusPill } from "@/components/portal/StatusPill";
import { PlanBadge } from "@/components/admin/PlanBadge";
import { adminDashboardOrdersQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { deliverReport, retryAudit } from "@/lib/orders.functions";
import type { AdminOrder } from "@/lib/orders.functions";

export const Route = createFileRoute("/admin/dashboard")({
  head: () => ({
    meta: [
      { title: "GEO Audit Admin · Shugert" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminDashboardPage,
});

const GOLD = "#C8A84B";
const TEAL = "#0D7680";

type StatusFilter = "all" | "review_needed" | "complete" | "failed";
type PlanFilter = "all" | "starter" | "pro" | "agency";

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}

function fmtMoney(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function fmtDate(iso: string | null) {
  if (!iso) return ", ";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

function todayLong() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const q = useQuery(adminDashboardOrdersQuery());
  const orders = useMemo(
    () => (q.data as { orders?: AdminOrder[] } | undefined)?.orders ?? [],
    [q.data],
  );

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("review_needed");
  const [planFilter, setPlanFilter] = useState<PlanFilter>("all");

  const stats = useMemo(() => {
    const monthStart = startOfMonth();
    const todayStart = startOfToday();
    let pendingProReview = 0;
    let completedToday = 0;
    let revenueCents = 0;
    for (const o of orders) {
      if (o.is_internal_audit) {
        // Internal QA / demo audits are excluded from revenue and review counters
        // so they don't pollute reporting. They still appear in the orders table.
        continue;
      }
      if (o.status === "review_needed" && o.plan_id.toLowerCase().includes("pro")) {
        pendingProReview += 1;
      }
      if (o.status === "complete") {
        const updated = o.paid_at ? new Date(o.paid_at).getTime() : 0;
        if (updated >= todayStart) completedToday += 1;
      }
      if (o.paid_at && new Date(o.paid_at).getTime() >= monthStart) {
        revenueCents += o.amount_cents ?? 0;
      }
    }
    return {
      total: orders.length,
      pendingProReview,
      completedToday,
      revenueCents,
    };
  }, [orders]);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (planFilter !== "all" && !o.plan_id.toLowerCase().includes(planFilter)) return false;
      return true;
    });
  }, [orders, statusFilter, planFilter]);

  const deliverM = useAuthedMutation(deliverReport, {
    invalidate: [INVALIDATE.adminOrders],
    onSuccess: (res) => {
      const r = res as { alreadyDelivered: boolean; email_sent_to?: string };
      toast.success(
        r.alreadyDelivered ? "Already delivered." : `Report delivered to ${r.email_sent_to} ✓`,
      );
    },
    onError: (e) => toast.error(e.message),
  });

  const retryM = useAuthedMutation(retryAudit, {
    invalidate: [INVALIDATE.adminOrders],
    onSuccess: () => toast.success("Retry triggered, audit is running again."),
    onError: (e) => toast.error(e.message),
  });

  const onDeliver = (o: AdminOrder) => {
    if (!confirm(`Send the final Pro report to ${o.email}? This marks the order complete.`)) return;
    deliverM.mutate({ order_id: o.id });
  };

  const onRetry = (o: AdminOrder) => {
    if (!confirm(`Re-run the audit for ${o.website_url}?`)) return;
    retryM.mutate({ order_id: o.id });
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground">GEO Audit Admin</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {todayLong()} ·{" "}
            <span style={{ color: stats.pendingProReview > 0 ? GOLD : undefined }}>
              {stats.pendingProReview} Pro report{stats.pendingProReview === 1 ? "" : "s"} pending
              review
            </span>
          </p>
        </div>
        <button
          onClick={() => q.refetch()}
          className="rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground hover:bg-card"
        >
          Refresh
        </button>
        <Link
          to="/admin/internal-audit"
          className="rounded-full px-4 py-2 text-xs font-semibold"
          style={{ background: GOLD, color: "#1a1206" }}
        >
          + Internal audit
        </Link>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total orders" value={String(stats.total)} />
        <StatCard
          label="Pending Pro review"
          value={String(stats.pendingProReview)}
          highlight={stats.pendingProReview > 0}
        />
        <StatCard label="Completed today" value={String(stats.completedToday)} />
        <StatCard label="Revenue this month" value={fmtMoney(stats.revenueCents)} />
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <FilterGroup
            label="Status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            options={[
              { value: "all", label: "All" },
              { value: "review_needed", label: "Needs review" },
              { value: "complete", label: "Complete" },
              { value: "failed", label: "Failed" },
            ]}
          />
          <FilterGroup
            label="Plan"
            value={planFilter}
            onChange={(v) => setPlanFilter(v as PlanFilter)}
            options={[
              { value: "all", label: "All" },
              { value: "starter", label: "Starter" },
              { value: "pro", label: "Pro" },
              { value: "agency", label: "Agency" },
            ]}
          />
          <span className="text-xs text-muted-foreground">
            Auto-refreshes every 30s · {filtered.length} shown
          </span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-border bg-card/30">
          {q.isPending ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : q.isError ? (
            <p className="p-6 text-sm text-destructive">{q.error?.message}</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No orders match the current filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-background/40 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <Th>Domain</Th>
                    <Th>Plan</Th>
                    <Th>Status</Th>
                    <Th>Score</Th>
                    <Th>Customer</Th>
                    <Th>Ordered</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 50).map((o) => (
                    <tr
                      key={o.id}
                      className="cursor-pointer border-b border-border/60 transition-colors hover:bg-card/60"
                      onClick={() => navigate({ to: "/admin/orders/$id", params: { id: o.id } })}
                    >
                      <Td>
                        <span className="font-semibold text-foreground">{o.website_url}</span>
                        <span className="block text-[10px] font-mono text-muted-foreground">
                          {o.order_number}
                        </span>
                        {o.is_internal_audit && (
                          <span
                            className="mt-1 inline-block rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em]"
                            style={{ borderColor: GOLD, color: GOLD }}
                          >
                            Internal
                          </span>
                        )}
                      </Td>
                      <Td>
                        <PlanBadge planId={o.plan_id} planName={o.plan_name} />
                      </Td>
                      <Td>
                        <StatusPill status={o.status} />
                      </Td>
                      <Td>
                        {o.audit_score != null ? (
                          <span className="font-mono text-foreground">{o.audit_score}/100</span>
                        ) : (
                          <span className="text-muted-foreground">, </span>
                        )}
                      </Td>
                      <Td className="text-muted-foreground">{o.email}</Td>
                      <Td className="text-muted-foreground">
                        {fmtDate(o.paid_at ?? o.created_at)}
                      </Td>
                      <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <RowActions
                          order={o}
                          onDeliver={() => onDeliver(o)}
                          onRetry={() => onRetry(o)}
                          deliverPending={deliverM.isPending}
                          retryPending={retryM.isPending}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="rounded-2xl border bg-card/30 p-5"
      style={{
        borderColor: highlight ? GOLD : undefined,
        boxShadow: highlight ? `0 0 0 1px ${GOLD}33 inset` : undefined,
      }}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div
        className="mt-2 font-display text-3xl font-semibold"
        style={{ color: highlight ? GOLD : undefined }}
      >
        {value}
      </div>
    </div>
  );
}

function FilterGroup<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              value === opt.value
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:bg-card"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-3 text-left font-medium ${className ?? ""}`}>{children}</th>;
}

function Td({
  children,
  className,
  onClick,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <td className={`px-4 py-3 align-middle ${className ?? ""}`} onClick={onClick}>
      {children}
    </td>
  );
}

function RowActions({
  order,
  onDeliver,
  onRetry,
  deliverPending,
  retryPending,
}: {
  order: AdminOrder;
  onDeliver: () => void;
  onRetry: () => void;
  deliverPending: boolean;
  retryPending: boolean;
}) {
  const isPro =
    order.plan_id.toLowerCase().includes("pro") || order.plan_id.toLowerCase().includes("agency");

  if (order.status === "review_needed" && isPro) {
    return (
      <button
        onClick={onDeliver}
        disabled={deliverPending || !order.report_url}
        title={order.report_url ? "Send final report" : "Attach a report URL first"}
        className="rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40"
        style={{ background: GOLD, color: "#1a1206" }}
      >
        {deliverPending ? "Sending…" : "Deliver Report"}
      </button>
    );
  }
  if (order.status === "complete" && order.report_url) {
    return (
      <Link
        to="/admin/orders/$id"
        params={{ id: order.id }}
        onClick={(e) => e.stopPropagation()}
        className="rounded-full px-4 py-1.5 text-xs font-semibold text-white"
        style={{ background: TEAL }}
      >
        View Report
      </Link>
    );
  }
  if (order.status === "failed") {
    return (
      <button
        onClick={onRetry}
        disabled={retryPending}
        className="rounded-full bg-destructive px-4 py-1.5 text-xs font-semibold text-destructive-foreground disabled:opacity-40"
      >
        {retryPending ? "Retrying…" : "Retry"}
      </button>
    );
  }
  return <span className="text-xs text-muted-foreground">, </span>;
}
